import { useCallback, useRef, useState, useEffect } from "react";

export const useTimeoutManager = () => {
  const [timeoutActive, setTimeoutActive] = useState<boolean>(false);
  const [apiInProgress, setApiInProgress] = useState<boolean>(false);
  const [countdownKey, setCountdownKey] = useState<string>(() => 
    Math.random().toString(36).substring(2) + Date.now().toString(36)
  );

  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const wasTimeoutActiveRef = useRef<boolean>(false);

  // ---------- TIMEOUT MANAGEMENT ----------
  const startTimeoutCountdown = useCallback(() => {
    // Clear existing countdown
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    
    setCountdownKey(() => Math.random().toString(36).substring(2) + Date.now().toString(36));
    setTimeoutActive(true);
  }, []);

  const resetTimeoutCountdown = useCallback(() => {
    setTimeoutActive(false);
    
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // ---------- API TRACKING ----------
  const handleApiStart = useCallback(() => {
    setApiInProgress(true);
    
    // Remember if timeout was active before API call
    wasTimeoutActiveRef.current = timeoutActive;
    
    // Clear countdown when API starts
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, [timeoutActive]);

  const handleApiComplete = useCallback(() => {
    setApiInProgress(false);
    
    // If timeout was active before API call, restart the countdown
    if (wasTimeoutActiveRef.current) {
      startTimeoutCountdown();
    }
  }, [startTimeoutCountdown]);

  // ---------- CLEANUP ----------
  const cleanup = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // Callback for CountdownEffect when timeout completes
  const handleTimeoutComplete = useCallback(() => {
    setTimeoutActive(false);
  }, []);

  // ---------- CLEANUP ON UNMOUNT ----------
  useEffect(() => {
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);

  return {
    // State
    timeoutActive,
    apiInProgress,
    countdownKey,
    
    // Actions
    startTimeoutCountdown,
    resetTimeoutCountdown,
    handleApiStart,
    handleApiComplete,
    cleanup,
    handleTimeoutComplete,
  };
};
